import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import RequiredMark from '@/shein-platform/components/RequiredMark';
import CategoryCascaderSelect from '@/shein-platform/components/CategoryCascaderSelect';
import { SpecValuePicker } from '@/shein-platform/modules/products/components/SpecSupplySection';
import {
  SIZE_CHART_BASE_KEY,
  applySizeChartTemplatePayload,
  buildSizeChartTemplatePayload,
  checkSizeMeasureValue,
  makeCustomSpecKey,
  partitionSizeMeasureAttrs,
  resolveSizeChartRows,
  sanitizeSizeMeasureInput,
  validateSizeChartForm,
} from '@/shein-platform/utils/sheinSizeChart';
import {
  attributeDisplayLabel,
  parseAttributeTemplate,
} from '@/shein-platform/utils/sheinAttributeTemplate';
import SheinSwitch from '@/shein-platform/components/SheinSwitch';
import { flattenLeafCategories } from '@/shein-platform/utils/sheinCategoryTree';
import { nextTemplatePriority } from '@/shein-platform/utils/sheinTemplatePriority';
import {
  conflictToastMessage,
  isTemplateVersionConflictError,
  parseTemplateVersion,
} from '@/shein-platform/utils/sheinTemplateVersionGuard';
import TemplateVersionConflictMark from '@/shein-platform/modules/products/components/TemplateVersionConflictMark';
import { sheinPlatformAPI } from '@/utils/api';
import { adminAPI } from '@/utils/adminApi';
import { showToast } from '@/utils/toast';
import { chartText } from '@/i18n/chartText';
import { useTranslation } from 'react-i18next';

function fetchSizeChartDetail(officialMode, shopId, templateId, templateScope) {
  if (officialMode) {
    return adminAPI.getOfficialSheinSizeChartTemplate(templateId);
  }
  return sheinPlatformAPI.getSizeChartTemplate(shopId, templateId, { scope: templateScope });
}

function fetchSizeChartList(officialMode, shopId, params) {
  if (officialMode) {
    return adminAPI.listOfficialSheinSizeChartTemplates(params);
  }
  return sheinPlatformAPI.listSizeChartTemplates(shopId, params);
}

function saveSizeChartTemplate(officialMode, shopId, templateId, body) {
  if (officialMode) {
    return templateId
      ? adminAPI.updateOfficialSheinSizeChartTemplate(templateId, body)
      : adminAPI.createOfficialSheinSizeChartTemplate(body);
  }
  return templateId
    ? sheinPlatformAPI.updateSizeChartTemplate(shopId, templateId, body)
    : sheinPlatformAPI.createSizeChartTemplate(shopId, body);
}

function fetchCategoryAttributeSchema(officialMode, shopId, productTypeId, categoryId, extra = {}) {
  const body = { product_type_id_list: [Number(productTypeId)] };
  const cat = Number(categoryId);
  if (Number.isFinite(cat) && cat > 0) body.category_id = cat;
  if (officialMode) {
    if (extra.shopType) body.shopType = extra.shopType;
    if (extra.categoryPathLabel) body.categoryPathLabel = extra.categoryPathLabel;
    return adminAPI.getOfficialSheinCatalogAttributeTemplate(body);
  }
  return sheinPlatformAPI.getAttributeTemplate(shopId, body, { forceRefresh: true });
}

/**
 * 新建/编辑尺码库：选类目 → 拉尺寸属性 → 填表 → 保存。
 */
export default function SizeChartTemplateCreateModal({
  open,
  shopId,
  templateId = null,
  templateScope = 'PERSONAL',
  officialMode = false,
  shopType = '',
  onClose,
  onSaved,
}) {
  useTranslation('workbench');
  const isEdit = Boolean(templateId);
  const pendingSeedRef = useRef(null);
  const [name, setName] = useState('');
  const [styleNo, setStyleNo] = useState('');
  const [remark, setRemark] = useState('');
  const [autoRuleEnabled, setAutoRuleEnabled] = useState(false);
  const [sortOrder, setSortOrder] = useState(0);
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [productTypeId, setProductTypeId] = useState('');
  const [categoryPathLabel, setCategoryPathLabel] = useState('');
  const [attrLoading, setAttrLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sizeAttr, setSizeAttr] = useState(null);
  const [measureAttrs, setMeasureAttrs] = useState([]);
  const [selectedSizeIds, setSelectedSizeIds] = useState([]);
  const [sizeChart, setSizeChart] = useState({});
  const [saving, setSaving] = useState(false);
  const [metaLoading, setMetaLoading] = useState(false);
  const [templateVersion, setTemplateVersion] = useState(null);
  const [versionConflict, setVersionConflict] = useState(false);
  const [reloadingVersion, setReloadingVersion] = useState(false);

  const categoryOptions = useMemo(
    () => flattenLeafCategories(categories).map((opt) => ({
      value: opt.categoryId,
      label: opt.label,
      productTypeId: opt.productTypeId,
    })),
    [categories],
  );

  useEffect(() => {
    if (!open) return;
    pendingSeedRef.current = null;
    setName('');
    setStyleNo('');
    setRemark('');
    setAutoRuleEnabled(false);
    setSortOrder(0);
    setCategoryId('');
    setProductTypeId('');
    setCategoryPathLabel('');
    setSizeAttr(null);
    setMeasureAttrs([]);
    setSelectedSizeIds([]);
    setSizeChart({});
    setAttrLoading(false);
    setDetailLoading(false);
    setSaving(false);
    setTemplateVersion(null);
    setVersionConflict(false);
    setReloadingVersion(false);
  }, [open, templateId]);

  useEffect(() => {
    if (!open || (!officialMode && !shopId)) {
      setCategories([]);
      return undefined;
    }
    let cancelled = false;
    setMetaLoading(true);
    const load = officialMode
      ? adminAPI.getOfficialSheinCatalogCategories({ shopType })
      : sheinPlatformAPI.getShopCategories(shopId);
    load
      .then((res) => {
        if (cancelled) return;
        setCategories(res?.categories ?? res?.items ?? res?.list ?? res ?? []);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      })
      .finally(() => {
        if (!cancelled) setMetaLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, shopId, officialMode, shopType]);

  useEffect(() => {
    if (!open || !templateId || (!officialMode && !shopId)) return undefined;
    let cancelled = false;
    setDetailLoading(true);
    fetchSizeChartDetail(officialMode, shopId, templateId, templateScope)
      .then((res) => {
        if (cancelled) return;
        const item = res?.item || res || {};
        const payload = item.payload && typeof item.payload === 'object' ? item.payload : {};
        setName(String(item.name || '').trim());
        setStyleNo(String(item.styleNo || '').trim());
        setRemark(String(item.remark || '').trim());
        setAutoRuleEnabled(item.autoRuleEnabled === true || item.autoRuleEnabled === 1);
        {
          const order = Number(item.sortOrder);
          setSortOrder(Number.isFinite(order) ? order : 0);
        }
        setCategoryId(String(item.categoryId || payload.categoryId || ''));
        setProductTypeId(String(item.productTypeId || payload.productTypeId || ''));
        setCategoryPathLabel(String(item.categoryPathLabel || payload.categoryPathLabel || ''));
        setTemplateVersion(parseTemplateVersion(item.version));
        setVersionConflict(false);
        const rows = Array.isArray(payload.sizeRows) ? payload.sizeRows : [];
        const ids = rows.map((row) => {
          if (row?.custom) {
            return makeCustomSpecKey(row.name || row.id || '');
          }
          return String(row?.id || '');
        }).filter(Boolean);
        // 若 sizeRows 缺失，回退 sizeChart 的 key
        const fallbackIds = Object.keys(payload.sizeChart || {}).map(String).filter(Boolean);
        pendingSeedRef.current = {
          selectedSizeIds: ids.length ? ids : fallbackIds,
          payload,
        };
      })
      .catch((e) => {
        if (cancelled) return;
        showToast(e?.message || chartText('photoLib.loadTemplateFailed', fixedUi("加载模板失败")), 'error');
        onClose?.();
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, shopId, templateId, templateScope, officialMode, onClose]);

  useEffect(() => {
    if (!open || (!officialMode && !shopId) || !productTypeId) {
      setSizeAttr(null);
      setMeasureAttrs([]);
      if (!templateId) {
        setSelectedSizeIds([]);
        setSizeChart({});
      }
      setAttrLoading(false);
      return undefined;
    }
    let cancelled = false;
    setAttrLoading(true);
    // 与发品页一致：整包响应交给 parseAttributeTemplate（勿先取 .data）
    fetchCategoryAttributeSchema(officialMode, shopId, productTypeId, categoryId, {
      shopType,
      categoryPathLabel,
    })
      .then((raw) => {
        if (cancelled) return;
        const tpl = parseAttributeTemplate(raw, { productTypeId });
        const sizeSale = tpl?.skuSaleAttrs?.[0] || null;
        const measures = tpl?.sizeAttrs || [];
        setSizeAttr(sizeSale);
        setMeasureAttrs(measures);
        const seed = pendingSeedRef.current;
        pendingSeedRef.current = null;
        if (seed) {
          const nextIds = (seed.selectedSizeIds || []).map(String).filter(Boolean);
          setSelectedSizeIds(nextIds);
          const rows = sizeSale?.attributeId
            ? resolveSizeChartRows(nextIds, sizeSale, [])
            : (measures.length ? [{ id: SIZE_CHART_BASE_KEY, name: '尺寸', custom: false }] : []);
          const { sizeChart: nextChart } = applySizeChartTemplatePayload(seed.payload || {}, rows, measures);
          setSizeChart(nextChart);
        } else {
          setSelectedSizeIds([]);
          setSizeChart({});
        }
        if (!measures.length) {
          const productCount = tpl?.productAttrs?.length || 0;
          showToast(
            productCount > 0
              ? chartText('sizeCreate.noSizeAttrHasProduct', '当前类目有商品属性，但无尺寸属性（type=2），无法创建尺码库')
              : chartText('sizeCreate.noSizeAttrPlatform', '平台未返回该类目尺寸属性，请确认类目或先同步店铺类目'),
            'warning',
          );
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setSizeAttr(null);
        setMeasureAttrs([]);
        showToast(e?.message || chartText('sizeCreate.loadAttrFail', '加载类目属性失败'), 'error');
      })
      .finally(() => {
        if (!cancelled) setAttrLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, shopId, productTypeId, categoryId, categoryPathLabel, templateId, officialMode, shopType]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  if (!open) return null;

  const hasSecondary = Boolean(sizeAttr?.attributeId);
  const sizeOptions = sizeAttr?.values || [];
  const sizeLabel = attributeDisplayLabel(sizeAttr) || fixedUi("尺寸");
  const saleRowCount = hasSecondary
    ? resolveSizeChartRows(selectedSizeIds, sizeAttr, []).length
    : 0;
  const { matrixAttrs, singleAttrs, unsupportedAttrs } = partitionSizeMeasureAttrs(
    measureAttrs,
    saleRowCount,
  );
  const visibleMeasureAttrs = measureAttrs.filter((a) => (
    singleAttrs.some((s) => String(s.attributeId) === String(a.attributeId))
    || matrixAttrs.some((m) => String(m.attributeId) === String(a.attributeId))
  ));
  const chartRows = hasSecondary
    ? resolveSizeChartRows(selectedSizeIds, sizeAttr, [])
    : (visibleMeasureAttrs.length
      ? [{ id: SIZE_CHART_BASE_KEY, name: '尺寸', custom: false }]
      : []);

  const onCategoryChange = (id) => {
    const hit = categoryOptions.find((opt) => String(opt.value) === String(id));
    setCategoryId(id ? String(id) : '');
    const nextTypeId = hit?.productTypeId && String(hit.productTypeId) !== '0'
      ? String(hit.productTypeId)
      : '';
    setProductTypeId(nextTypeId);
    setCategoryPathLabel(hit?.label || '');
    if (id && !nextTypeId) {
      showToast(
        officialMode
          ? chartText('sizeCreate.missingTypeLeaf', fixedUi("该类目缺少 product_type_id，无法查询属性，请换一个末级类目"))
          : chartText('sizeCreate.missingTypeSync', '该类目缺少 product_type_id，无法查询属性，请先在店铺信息同步类目'),
        'warning',
      );
    }
    if (!isEdit && nextTypeId && (officialMode || shopId)) {
      fetchSizeChartList(officialMode, shopId, {
        scope: templateScope,
        productTypeId: nextTypeId,
        page: 1,
        pageSize: 100,
        ...(officialMode ? { shopType } : {}),
      }).then((res) => {
        setSortOrder(nextTemplatePriority(res?.items));
      }).catch(() => {
        setSortOrder(0);
      });
    }
  };

  const updateCell = (rowId, attrId, value) => {
    setSizeChart((prev) => ({
      ...prev,
      [rowId]: {
        ...(prev[rowId] || {}),
        [attrId]: value,
      },
    }));
  };

  const applySizeChartItem = (item) => {
    const payload = item.payload && typeof item.payload === 'object' ? item.payload : {};
    setName(String(item.name || '').trim());
    setStyleNo(String(item.styleNo || '').trim());
    setRemark(String(item.remark || '').trim());
    setAutoRuleEnabled(item.autoRuleEnabled === true || item.autoRuleEnabled === 1);
    {
      const order = Number(item.sortOrder);
      setSortOrder(Number.isFinite(order) ? order : 0);
    }
    setCategoryId(String(item.categoryId || payload.categoryId || ''));
    setProductTypeId(String(item.productTypeId || payload.productTypeId || ''));
    setCategoryPathLabel(String(item.categoryPathLabel || payload.categoryPathLabel || ''));
    setTemplateVersion(parseTemplateVersion(item.version));
    const rows = Array.isArray(payload.sizeRows) ? payload.sizeRows : [];
    const ids = rows.map((row) => {
      if (row?.custom) {
        return makeCustomSpecKey(row.name || row.id || '');
      }
      return String(row?.id || '');
    }).filter(Boolean);
    const fallbackIds = Object.keys(payload.sizeChart || {}).map(String).filter(Boolean);
    pendingSeedRef.current = {
      selectedSizeIds: ids.length ? ids : fallbackIds,
      payload,
    };
  };

  const applyPendingSizeSeed = (sizeSale, measures) => {
    const seed = pendingSeedRef.current;
    pendingSeedRef.current = null;
    if (!seed) return;
    const nextIds = (seed.selectedSizeIds || []).map(String).filter(Boolean);
    setSelectedSizeIds(nextIds);
    const chartRowList = sizeSale?.attributeId
      ? resolveSizeChartRows(nextIds, sizeSale, [])
      : (measures.length ? [{ id: SIZE_CHART_BASE_KEY, name: '尺寸', custom: false }] : []);
    const { sizeChart: nextChart } = applySizeChartTemplatePayload(seed.payload || {}, chartRowList, measures);
    setSizeChart(nextChart);
  };

  const reloadTemplateFromServer = async () => {
    if (!templateId || (!officialMode && !shopId)) return;
    setReloadingVersion(true);
    try {
      const res = await fetchSizeChartDetail(officialMode, shopId, templateId, templateScope);
      const item = res?.item || res || {};
      applySizeChartItem(item);
      const ptId = String(item.productTypeId || item.payload?.productTypeId || '');
      const catId = String(item.categoryId || item.payload?.categoryId || '');
      if (ptId && (officialMode || shopId)) {
        const raw = await fetchCategoryAttributeSchema(officialMode, shopId, ptId, catId, {
          shopType,
          categoryPathLabel: String(item.categoryPathLabel || item.payload?.categoryPathLabel || ''),
        });
        const tpl = parseAttributeTemplate(raw, { productTypeId: ptId });
        const sizeSale = tpl?.skuSaleAttrs?.[0] || null;
        const measures = tpl?.sizeAttrs || [];
        setSizeAttr(sizeSale);
        setMeasureAttrs(measures);
        applyPendingSizeSeed(sizeSale, measures);
      }
      setVersionConflict(false);
      showToast(chartText('sizeCreate.reloaded', '已重新加载尺码库'), 'success');
    } catch (e) {
      showToast(e?.message || chartText('photoLib.reloadFailed', fixedUi("重新加载失败")), 'error');
    } finally {
      setReloadingVersion(false);
    }
  };

  const submit = async () => {
    if (!officialMode && !shopId) {
      showToast(chartText('returnList.pickShop', fixedUi("请先选择店铺")), 'error');
      return;
    }
    const styleNoValue = String(styleNo || '').trim();
    if (!styleNoValue) {
      showToast(chartText('photoLib.needStyleNo', fixedUi("请填写款号")), 'error');
      return;
    }
    const title = String(name || '').trim();
    if (!title) {
      showToast(chartText('photoLib.needName', fixedUi("请填写模板名称")), 'error');
      return;
    }
    if (!productTypeId) {
      showToast(chartText('rrpLib.needCategory', fixedUi("请选择类目")), 'error');
      return;
    }
    if (!measureAttrs.length) {
      showToast(chartText('sizeCreate.noSizeAttr', '当前类目无尺寸属性'), 'error');
      return;
    }
    if (hasSecondary && !selectedSizeIds.length) {
      showToast(chartText('sizeCreate.pickNamed', `请选择${sizeLabel}`, { label: sizeLabel === '尺寸' ? chartText('sizeChartPanel.sizeWord', '尺寸') : sizeLabel }), 'error');
      return;
    }
    if (!chartRows.length) {
      showToast(chartText('sizeCreate.finishSpec', '请先完成规格选择'), 'error');
      return;
    }
    if (attrLoading || !measureAttrs.length) {
      showToast(attrLoading ? chartText('sizeCreate.attrLoadingSave', '类目尺寸属性加载中，请稍后再保存') : chartText('sizeCreate.noSizeAttr', '当前类目无尺寸属性'), 'error');
      return;
    }
    const sizeCheck = validateSizeChartForm({
      sizeChart,
      measureAttrs,
      sizeAttr,
      selectedSizeIds,
    });
    if (!sizeCheck.ok) {
      showToast(sizeCheck.message || chartText('saveTpl.chartInvalid', '尺码表不符合平台填写要求'), 'error');
      return;
    }
    const payload = buildSizeChartTemplatePayload({
      sizeChart,
      chartRows,
      measureAttrs,
      sizeSaleAttributeId: sizeAttr?.attributeId || '',
      productTypeId,
      categoryId,
      categoryPathLabel,
    });
    setSaving(true);
    try {
      const body = {
        name: title,
        styleNo: styleNoValue,
        remark: String(remark || '').trim() || undefined,
        productTypeId,
        categoryId,
        categoryPathLabel,
        autoRuleEnabled,
        sortOrder: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
        sizeSaleAttributeId: sizeAttr?.attributeId || '',
        payload,
      };
      if (officialMode) {
        body.shopType = shopType;
      }
      if (!isEdit && !officialMode) {
        body.scope = templateScope;
      }
      if (isEdit && templateVersion != null) {
        body.version = templateVersion;
      }
      const res = await saveSizeChartTemplate(officialMode, shopId, isEdit ? templateId : null, body);
      if (res?.item && parseTemplateVersion(res.item.version) != null) {
        setTemplateVersion(parseTemplateVersion(res.item.version));
      }
      setVersionConflict(false);
      showToast(isEdit ? chartText('photoLib.updated', fixedUi("模板已更新")) : chartText('photoLib.createdTpl', fixedUi("模板已创建")), 'success');
      onSaved?.();
      onClose?.();
    } catch (e) {
      if (isEdit && isTemplateVersionConflictError(e)) {
        showToast(conflictToastMessage([{ type: 'sizeChart' }]), 'warning', 3000);
        setVersionConflict(true);
      } else {
        showToast(e?.message || (isEdit ? chartText('photoLib.updateFailed', fixedUi("更新失败")) : chartText('photoLib.createFailed', '创建失败')), 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="spo-batch-modal spo-size-tpl-modal" role="presentation">
      <button
        type="button"
        className="spo-batch-modal__backdrop"
        aria-label={chartText('moduleShell.close', fixedUi("关闭"))}
        onClick={() => { if (!saving) onClose?.(); }}
      />
      <div
        className="spo-batch-modal__panel spo-size-tpl-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-size-tpl-create-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="spo-batch-modal__head">
          <h2 id="spo-size-tpl-create-title">
            {isEdit
              ? (officialMode ? chartText('sizeCreate.editOfficial', '编辑官方尺码模板') : chartText('sizeCreate.editLib', '编辑尺码库'))
              : (officialMode ? chartText('sizeCreate.createOfficial', '新建官方尺码模板') : chartText('sizeCreate.createLib', '新建尺码库'))}
          </h2>
          <button type="button" className="spo-batch-modal__close" aria-label={chartText('moduleShell.close', fixedUi("关闭"))} disabled={saving} onClick={onClose}>×</button>
        </div>
        <div className="spo-batch-modal__body">
          <div className="spo-wizard__form spo-template-create-form">
            <label className="spo-field spo-field--full">
              <span className="spo-field__label">{chartText('productTemplate.colStyleNo', fixedUi("款号"))} <RequiredMark /></span>
              <input
                className="spo-input"
                value={styleNo}
                disabled={saving || detailLoading}
                maxLength={128}
                placeholder={chartText('photoLib.styleNoPh', fixedUi("请输入款号"))}
                onChange={(e) => setStyleNo(e.target.value)}
              />
            </label>
            <label className={`spo-field spo-field--full${versionConflict ? ' spo-field--version-conflict' : ''}`}>
              <span className="spo-field__label">
                {fixedUi("模板名称")} <RequiredMark />
                <TemplateVersionConflictMark
                  active={versionConflict}
                  label={chartText('photoLib.updatedMark', '已更新')}
                  reloading={reloadingVersion}
                  onReload={reloadTemplateFromServer}
                />
              </span>
              <input
                className="spo-input"
                value={name}
                disabled={saving || detailLoading}
                maxLength={128}
                placeholder={chartText('sizeCreate.namePh', '如：女装上衣基础尺码表')}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="spo-field spo-field--full">
              <span className="spo-field__label">{chartText('productTemplate.colRemark', fixedUi("备注"))}</span>
              <input
                className="spo-input"
                value={remark}
                disabled={saving || detailLoading}
                maxLength={200}
                placeholder={chartText('photoLib.optional', fixedUi("可选"))}
                onChange={(e) => setRemark(e.target.value)}
              />
            </label>
            <div className="spo-field spo-field--full spo-template-auto-rule">
              <div className="spo-template-auto-rule__switch">
                <SheinSwitch
                  checked={autoRuleEnabled}
                  disabled={saving || detailLoading}
                  ariaLabel={chartText('productTemplate.colAutoRule', '自动规则')}
                  onChange={setAutoRuleEnabled}
                />
                <span>{chartText('productTemplate.colAutoRule', fixedUi("自动规则"))}</span>
              </div>
              <p className="spo-wizard__inline-hint">
                {chartText('sizeCreate.autoHint', '开启后，发品选中该类目时自动带出。优先级仅在同类目内比较，数字越小越优先；同类目不可重复，不同类目互不影响。')}
              </p>
              <label className="spo-field">
                <span className="spo-field__label">{chartText('productTemplate.colPriority', fixedUi("优先级"))}</span>
                <input
                  className="spo-input"
                  type="number"
                  step={1}
                  value={sortOrder}
                  disabled={saving || detailLoading}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    setSortOrder(Number.isFinite(next) ? next : 0);
                  }}
                />
              </label>
            </div>
            <label className="spo-field spo-field--full">
              <span className="spo-field__label">{chartText('rrpLib.categoryApply', fixedUi("适用类目"))} <RequiredMark /></span>
              <CategoryCascaderSelect
                categories={categories}
                value={categoryId}
                label={categoryPathLabel}
                disabled={saving || detailLoading || metaLoading || (!officialMode && !shopId)}
                placeholder={
                  officialMode
                    ? (metaLoading || detailLoading ? chartText('templateEditor.loadingCats', '加载类目中…') : chartText('rrpLib.categorySearch', fixedUi("请选择或搜索类目")))
                    : (!shopId ? chartText('returnList.pickShop', fixedUi("请先选择店铺")) : (metaLoading || detailLoading ? chartText('tiktokPublish.loading', fixedUi("加载中…")) : chartText('rrpLib.categorySearch', fixedUi("请选择或搜索类目"))))
                }
                onChange={onCategoryChange}
              />
            </label>
          </div>

          {detailLoading ? (
            <p className="spo-wizard__inline-hint">{chartText('photoLib.loadingTemplate', fixedUi("正在加载模板…"))}</p>
          ) : null}

          {attrLoading && productTypeId ? (
            <p className="spo-wizard__inline-hint">{chartText('sizeCreate.loadingSizeAttrs', fixedUi("正在加载类目尺寸属性…"))}</p>
          ) : null}

          {hasSecondary && measureAttrs.length > 0 ? (
            <div className="spo-template-create-sizes">
              <span className="spo-field__label">{sizeLabel === '尺寸' ? chartText('sizeChartPanel.sizeWord', fixedUi("尺寸")) : sizeLabel} <RequiredMark /></span>
              <SpecValuePicker
                options={sizeOptions}
                value={selectedSizeIds}
                onChange={setSelectedSizeIds}
                placeholder={chartText('sizeCreate.pickNamed', `请选择${sizeLabel}`, { label: sizeLabel === '尺寸' ? chartText('sizeChartPanel.sizeWord', '尺寸') : sizeLabel })}
                disabled={saving || attrLoading}
                emptyText={chartText('sizeCreate.noPlatformOpt', '暂无平台选项')}
                disabledHint={attrLoading ? chartText('tiktokPublish.loading', fixedUi("加载中…")) : chartText('sizeChartPanel.notSelectable', '暂不可选')}
              />
            </div>
          ) : null}

          {unsupportedAttrs.length > 0 ? (
            <p className="spo-wizard__inline-hint">
              {chartText('sizeCreate.singleSkipShort', `平台单选尺寸属性「${unsupportedAttrs.map((a) => attributeDisplayLabel(a) || a.attributeId).join('、')}」在多尺码下 API 无法提交（已自动跳过）`, { names: unsupportedAttrs.map((a) => attributeDisplayLabel(a) || a.attributeId).join('、') })}
              
            </p>
          ) : null}

          {chartRows.length > 0 && visibleMeasureAttrs.length > 0 ? (
            <div className="spo-publish__table-wrap spo-publish__table-wrap--scroll">
              {singleAttrs.length > 0 ? (
                <p className="spo-wizard__inline-hint">
                  {chartText('sizeChartPanel.sharedCols', '标「共用」的列为平台单选：合并填写一次')}
                </p>
              ) : null}
              <table className="spo-publish__table spo-size-chart-table">
                <thead>
                  <tr>
                    <th className="spo-size-chart-table__size">
                      {hasSecondary ? sizeLabel : chartText('sizeChartPanel.sizeWord', fixedUi("尺寸"))}
                    </th>
                    {visibleMeasureAttrs.map((attr) => {
                      const shared = singleAttrs.some((a) => String(a.attributeId) === String(attr.attributeId));
                      return (
                        <th key={attr.attributeId}>
                          {attributeDisplayLabel(attr) || attr.attributeId}
                          {attr.required ? <span className="spo-size-chart-table__req"> *</span> : null}
                          {shared ? <span className="spo-size-chart-table__shared"> {chartText('sizeChartPanel.shared', fixedUi("共用"))}</span> : null}
                          <span className="spo-size-chart-table__unit"> (cm)</span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {chartRows.map((row, rowIndex) => (
                    <tr key={row.id}>
                      <td className="spo-size-chart-table__size">{row.name}</td>
                      {visibleMeasureAttrs.map((attr) => {
                        const shared = singleAttrs.some((a) => String(a.attributeId) === String(attr.attributeId));
                        if (shared) {
                          if (rowIndex !== 0) return null;
                          const fromBase = String(sizeChart?.[SIZE_CHART_BASE_KEY]?.[attr.attributeId] || '').trim();
                          const fallback = chartRows
                            .map((r) => String(sizeChart?.[r.id]?.[attr.attributeId] || '').trim())
                            .find(Boolean) || '';
                          const cell = fromBase || fallback;
                          const invalid = Boolean(cell) && !checkSizeMeasureValue(cell, attr).ok;
                          return (
                            <td
                              key={attr.attributeId}
                              rowSpan={chartRows.length}
                              className="spo-size-chart-table__merged"
                            >
                              <input
                                className="spo-input"
                                type="text"
                                inputMode="decimal"
                                disabled={saving}
                                value={cell}
                                onChange={(e) => updateCell(
                                  SIZE_CHART_BASE_KEY,
                                  attr.attributeId,
                                  sanitizeSizeMeasureInput(e.target.value, attr),
                                )}
                                placeholder="—"
                                aria-invalid={invalid ? 'true' : undefined}
                                title={chartText('sizeChartPanel.sharedTitle', '所有尺码共用此值')}
                              />
                            </td>
                          );
                        }
                        const cell = sizeChart?.[row.id]?.[attr.attributeId] || '';
                        const invalid = Boolean(cell) && !checkSizeMeasureValue(cell, attr).ok;
                        return (
                          <td key={attr.attributeId}>
                            <input
                              className="spo-input"
                              type="text"
                              inputMode="decimal"
                              disabled={saving}
                              value={cell}
                              onChange={(e) => updateCell(
                                row.id,
                                attr.attributeId,
                                sanitizeSizeMeasureInput(e.target.value, attr),
                              )}
                              placeholder="—"
                              aria-invalid={invalid ? 'true' : undefined}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
        <div className="spo-batch-modal__foot">
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" disabled={saving} onClick={onClose}>
            {chartText('shop.cancel', '取消')}
          </button>
          <button
            type="button"
            className="spo-btn spo-btn--primary spo-btn--sm"
            disabled={saving || detailLoading || (!officialMode && !shopId)}
            onClick={submit}
          >
            {saving ? chartText('tiktokPublish.saving', fixedUi("保存中…")) : (isEdit ? chartText('photoLib.saveChanges', '保存修改') : chartText('shop.save', fixedUi("保存")))}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
